// src/components/elevenlabs/ElevenLabsJobsPage.tsx
// Jobs listing with filter + detail panel for ElevenLabs async jobs.

import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

import {
  ApiError, listJobs,
  type ELJob, type ELJobType, type ELJobStatus,
} from '@/lib/api_elevenlabs';
import { AudioPlayer } from '@/components/shared/AudioPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function ElevenLabsJobsPage({ addNotification }: { addNotification?: Notifier }) {
  const [jobs, setJobs] = useState<ELJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);
  const [selected, setSelected] = useState<ELJob | null>(null);

  // Filters
  const [filterType, setFilterType] = useState<ELJobType | ''>('');
  const [filterStatus, setFilterStatus] = useState<ELJobStatus | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit, offset: (page - 1) * limit };
      if (filterType) params.job_type = filterType;
      if (filterStatus) params.status = filterStatus;
      const res = await listJobs(params as any);
      setJobs(res.items);
      setTotal(res.count);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error cargando jobs', message: e.message });
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (apiMissing) return <ApiKeyMissing service="ElevenLabs" />;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value as any); setPage(1); }}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
        >
          <option value="">Todos los tipos</option>
          {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value as any); setPage(1); }}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
        >
          <option value="">Todos los estados</option>
          {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <button onClick={load} className="p-1.5 rounded-lg bg-white/5 text-white/50 hover:text-white/80">
          <RefreshCw size={14} />
        </button>

        <span className="text-xs text-white/40 ml-auto">{total} jobs</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-white/40" /></div>
      ) : jobs.length === 0 ? (
        <p className="text-center text-sm text-white/40 py-10">Sin jobs</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-white/40 border-b border-white/10">
                <th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Creado</th>
                <th className="py-2">ID</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.id}
                  onClick={() => setSelected(j)}
                  className={`border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                    selected?.id === j.id ? 'bg-white/10' : ''
                  }`}
                >
                  <td className="py-2 pr-3 text-white/70">{j.job_type}</td>
                  <td className="py-2 pr-3"><JobStatusBadge status={j.status} /></td>
                  <td className="py-2 pr-3 text-white/50">{new Date(j.created_at).toLocaleString()}</td>
                  <td className="py-2 text-white/30 font-mono text-xs">{j.id.slice(0, 12)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 text-xs rounded bg-white/5 text-white/60 disabled:opacity-30"
          >
            Anterior
          </button>
          <span className="text-xs text-white/40 self-center">Pág. {page} / {Math.ceil(total / limit)}</span>
          <button
            disabled={page >= Math.ceil(total / limit)}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 text-xs rounded bg-white/5 text-white/60 disabled:opacity-30"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div className="o-card border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white/80">{selected.job_type}</h3>
            <JobStatusBadge status={selected.status} />
          </div>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <dt className="text-white/40">Job ID</dt>
            <dd className="text-white/70 font-mono">{selected.id}</dd>
            <dt className="text-white/40">Creado</dt>
            <dd className="text-white/70">{new Date(selected.created_at).toLocaleString()}</dd>
            {selected.completed_at && (
              <>
                <dt className="text-white/40">Completado</dt>
                <dd className="text-white/70">{new Date(selected.completed_at).toLocaleString()}</dd>
              </>
            )}
            {selected.error_message && (
              <>
                <dt className="text-white/40">Error</dt>
                <dd className="text-red-400">{selected.error_message}</dd>
              </>
            )}
          </dl>
          {selected.status === 'completed' && selected.audio_url && (
            <div className="mt-3">
              <AudioPlayer src={selected.audio_url} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const JOB_TYPES: ELJobType[] = ['podcast', 'dubbing'];
const JOB_STATUSES: ELJobStatus[] = ['pending', 'processing', 'completed', 'failed'];
